import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { isValidId } from '../u2000Core/tools/U2000IdUtils';
import { NS } from './U3121';
import {
  doAdoptGoogleDueDate,
  doAdoptSubProjectGoogleDueDate,
  luHandleAppiError,
  parseU3121AdoptGoogleDueDateResult,
  type U3121AdoptGoogleDueDateResult,
} from './U3121Service';

/** Reprend la date Google Calendar vers l'échéance du projet (BL-0629). */
export function useU3121AdoptGoogleDueDateHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const handleError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      errorResult.genericErrors?.forEach((err) => toast.error(err.text, err.title));
    },
    [t, toast],
  );

  const adoptGoogleDueDate = useCallback(
    async (projectId: string): Promise<U3121AdoptGoogleDueDateResult | null> => {
      const pid = projectId?.trim();
      if (!pid || !isValidId(pid)) return null;
      try {
        const response = await doAdoptGoogleDueDate(axios, pid);
        return parseU3121AdoptGoogleDueDateResult(response.data?.result);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  const adoptSubProjectGoogleDueDate = useCallback(
    async (projectId: string, subProjectId: string): Promise<U3121AdoptGoogleDueDateResult | null> => {
      const pid = projectId?.trim();
      const spid = subProjectId?.trim();
      if (!pid || !isValidId(pid) || !spid || !isValidId(spid)) return null;
      try {
        const response = await doAdoptSubProjectGoogleDueDate(axios, pid, spid);
        return parseU3121AdoptGoogleDueDateResult(response.data?.result);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  return { adoptGoogleDueDate, adoptSubProjectGoogleDueDate };
}
